import { Box, Button, Tab, Tabs } from "@mui/material";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import { MecordColors } from "Constants/Color";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BranchDetails from "./BranchDetails";
import KYCDetails from "./KYCDetails";
import ProductDetails from "./ProductDetails";
import SupplierDetails from "./SupplierDetails";
import { fetchSupplierById } from "slices/Masters/supplier/thunk";
import { useDispatch } from "react-redux";
import { Container } from "react-bootstrap";
import { toast } from "react-toastify";
import LoadingSpinner from "components/LoadingSpinner";
import FormHeader from "components/FormAndDetailHeader/FormAndDetailHeader";
import CancelBackButton from "Common/Components/Buttons/CancelBackButton";

type SupplierTab = "supplier" | "kyc" | "branch" | "price";

const SupplierViewDetails = () => {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<SupplierTab>("supplier");
  const dispatch = useDispatch<any>();
  const [data, setData] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const tabsOrder = ["supplier", "kyc", "branch", "price"] as const;
  const currentTabIndex = tabsOrder.indexOf(activeTab);

  const handleNext = () => {
    if (currentTabIndex < tabsOrder.length - 1) {
      setActiveTab(tabsOrder[currentTabIndex + 1]);
    }
  };

  const handlePrevious = () => {
    if (currentTabIndex > 0) {
      setActiveTab(tabsOrder[currentTabIndex - 1]);
    }
  };

  useEffect(() => {
    const fetchSupplierData = async () => {
      try {
        setLoading(true);
        setError(null);
        if (id) {
          const supplierData = await dispatch(
            fetchSupplierById(id as string) as any
          ).unwrap();
          setData(supplierData || {});
        } else {
          setError("No supplier ID provided");
          toast.error("No supplier ID provided");
        }
      } catch (error: any) {
        console.error("Error fetching supplier details:", error);
        const errorMessage =
          error?.message || "Failed to fetch supplier details";
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchSupplierData();
  }, [id, dispatch]);

  if (error) {
    return (
      <Container>
        <div
          className="d-flex flex-column justify-content-center align-items-center"
          style={{ height: "200px" }}
        >
          <div className="text-center">
            <h5 className="text-danger mb-3">Error Loading Supplier Details</h5>
            <p className="text-muted mb-3">{error}</p>
            <button
              className="btn btn-primary"
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <PageContainer>
      <FormHeader
        title="View Supplier Details"
        firstLabel="Supplier"
        secondLabel="View Supplier Details"
        handleArrowClick={() => navigate("/master/supplier")}
      />
      <hr />

      {loading ? (
        <LoadingSpinner />
      ) : (
        <Box>
          <Tabs
            value={currentTabIndex}
            sx={{
              "& .MuiTab-root": {
                fontFamily: "Poppins, sans-serif",
                fontSize: "14px",
                textTransform: "none",
                color: MecordColors.DefaultText,
                "&.Mui-selected": {
                  color: MecordColors.ThemeBGColor,
                  fontWeight: 500,
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: MecordColors.ThemeBGColor,
              },
            }}
            onChange={(_, newValue) => setActiveTab(tabsOrder[newValue])}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Supplier Details" />
            <Tab label="KYC" />
            <Tab label="Branch Details" />
            <Tab label="Product Details" />
          </Tabs>

          {/* Tab Content */}
          <Box sx={{ mt: 2 }}>
            {activeTab === "supplier" && <SupplierDetails data={data} />}
            {activeTab === "kyc" && <KYCDetails data={data} />}
            {activeTab === "branch" && <BranchDetails data={data} />}
            {activeTab === "price" && <ProductDetails data={data} />}
          </Box>
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2 }}>
        {/* Previous */}
        <Button
          variant="outlined"
          disabled={currentTabIndex === 0}
          onClick={handlePrevious}
          sx={{
            color: MecordColors.ThemeBGColor,
            borderColor: MecordColors.ThemeBGColor,
            "&:hover": {
              backgroundColor: MecordColors.LightThemeBGColor,
              borderColor: MecordColors.ThemeBGColor,
            },
            textTransform: "none",
          }}
        >
          Previous
        </Button>

        {/* Cancel */}
        <Button
          variant="outlined"
          onClick={() => navigate(-1)} // 👈 go back
          sx={{
            color: MecordColors.DefaultText,
            borderColor: MecordColors.DefaultText,
            "&:hover": {
              backgroundColor: "#f5f5f5",
              borderColor: MecordColors.DefaultText,
            },
            textTransform: "none",
          }}
        >
          Cancel
        </Button>

        {/* Next */}
        <Button
          variant="outlined"
          disabled={currentTabIndex === tabsOrder.length - 1}
          onClick={handleNext}
          sx={{
            color: MecordColors.ThemeBGColor,
            borderColor: MecordColors.ThemeBGColor,
            "&:hover": {
              backgroundColor: MecordColors.LightThemeBGColor,
              borderColor: MecordColors.ThemeBGColor,
            },
            textTransform: "none",
          }}
        >
          Next
        </Button>
      </Box>
    </PageContainer>
  );
};

export default SupplierViewDetails;
